import{
  randomConjugationPrompt,
} from "@/verbprompts.js";
import {
  VERB_TENSES,
  VERB_CATEGORY,
  VERB_PERSON,
  VERB_NUMBER,
  VOWELS,
  SAMPLE_VERBS,
  VERB_FORMS
} from "@/constants.js";
import { endsWith, first, result } from "lodash";
import * as conjugationTables from "./conjugationtables.js";
import {
  letterIs,
  letterIsCFGMP,
  letterIsNRL,
  lastLetter,
  letterIsS,
  letterIsVowel,
  firstLetter,
  firstVowelGroup,
  startsWith, beginsWithS
} from "./utility.js";
const past_tense_prefix = "do";
const interrogative_prefix = "an";
const tenses = Object.values(VERB_TENSES);
const persons = Object.values(VERB_PERSON);
const numbers = Object.values(VERB_NUMBER);

function getNegativePrefix(tense){
  if(tense === VERB_TENSES.imperative) return "ná "
  else return "ní "
}

//takes a verb stem and slenderises the vowels within
function slenderiseVowels(verbStem) {
  if (verbStem.length <= 0) return "";
  var vowelGroup = firstVowelGroup(verbStem);
  if (vowelGroup.length < 2) {
    verbStem = verbStem.replace(vowelGroup, vowelGroup + "i");
  } else if ("eé".indexOf(firstLetter(vowelGroup)) >= 0) {
    if (firstLetter(vowelGroup) === "o") {
      verbStem = verbStem.replace(vowelGroup, vowelGroup + "i");
    } else {
      verbStem = verbStem.replace(vowelGroup, firstLetter(vowelGroup) + "i");
    }
  } else if (vowelGroup === "ia") {
    verbStem = verbStem.replace(vowelGroup, "éi");
  } else if (vowelGroup === "io") {
    verbStem = verbStem.replace(vowelGroup, "i");
  } else {
    verbStem = verbStem.replace(vowelGroup, vowelGroup + "i");
  }
  return verbStem;
}

export function generateStem(dictionaryForm, category, tense) {
  var trimLength;
  switch (category) {
    case VERB_CATEGORY.first:
      trimLength = 4;
      break;
    case VERB_CATEGORY.second:
      trimLength = 3;
      break;
    case VERB_CATEGORY.third:
      if (tense === VERB_TENSES.future || tense === VERB_TENSES.conditional) {
        trimLength = 7;
        return slenderiseVowels(
          dictionaryForm.substring(0, dictionaryForm.length - trimLength)
        );
      } else trimLength = 3;
    case VERB_CATEGORY.fourth:
      if (tense === VERB_TENSES.future || tense === VERB_TENSES.conditional) {
        trimLength = 6;
        return dictionaryForm.substring(0, dictionaryForm.length - trimLength);
      } else {
        trimLength = 3;
        return dictionaryForm.substring(0, dictionaryForm.length - trimLength);
      }
    default:
      return dictionaryForm
      break;
  }
  return dictionaryForm.substring(0, dictionaryForm.length - trimLength);
}

function defaultConjugation(root, negative, interrogative, tense) {
  if (negative) {
    return getNegativePrefix(tense) + leniteRoot(root);
  } else if (interrogative) {
    return interrogative_prefix + " " + eclipse(root);
  } else {
    return root;
  }
}

function pastTense(root, negative, interrogative, sPreterite) {
  var conjugatedForm = "";
  if (sPreterite) {
    conjugatedForm += root;
  } else if (negative) {
    conjugatedForm += "níor " + leniteRoot(root);
  } else if (interrogative) {
    conjugatedForm += "ar " + leniteRoot(root);
  } else {
    conjugatedForm += past_tense_prefix + " " + leniteRoot(root);
  }
  return conjugatedForm;
}

//Given a conjugated form and the root form of the verb, return list of all the matching Tense/Person/Number cominations that produce the form
export function getMatchingForms(root, formsToMatch, category, depForm) {
  var resultSet = new Set();
  var result = [];

  var allForms = enumerateAllConjugations(category, depForm, root);
  console.log(allForms)
  allForms.forEach((tense, index) => {
    tense.forEach((tableCell, index2) => {
      formsToMatch.forEach(form => {
        if (tableCell.indexOf(form) >= 0) {
          var par = [];
          par.push(index, index2);
          if (!resultSet.has(par.toString())) {
            resultSet.add(par.toString());
            result.push(par);
          }
        }
      });
    });
  });
  // return conjugationParameters(result)
  return formatSetResult(result, depForm);
  // return Array.from(result);
}

//returns the set of tense,person,number via index based lookups
function formatSetResult(set, depForm) {
  var result = [];
  set.forEach(str => {
    var tense = tenses[str[0]];
    var number = numbers[Math.floor(str[1] / 3)];
    var person = persons[str[1] % 3];
    var par = [];
    par.push(tense, person, number, depForm);
    result.push(par);
  });
  console.log(result);
  return result;
}

//Returns all the conjugated forms for a given verb within a given tense. Used to display all results in a table.
export function enumerateConjugations(category, tense, depForm, root) {
  var result = [];
  numbers.forEach(num => {
    persons.forEach(person => {
      result.push(conjugate(person, num, category, tense, depForm, root));
    });
  });
  return result;
}

//Returns all the conjugated forms for a given verb ACROSS  ALL TENSES.
export function enumerateAllConjugations(category, depForm, root) {
  var result = [];
  var word = SAMPLE_VERBS.find(v => v.verb === root)
  tenses.forEach(tense => {
      result.push(enumerateConjugations(category,tense,depForm,root));
  });
  console.log(result)
  return result;
}

function getTableIndex(person, number) {
  var pIndex = persons.indexOf(person);
  var nIndex = numbers.indexOf(number);
  return nIndex * 3 + pIndex;
}

function handleInterrogative(root,tense){
  return interrogative_prefix + " " + eclipse(root);
}

function handleNegative(root,tense){
  return getNegativePrefix(tense) + lenite(root);
}

function conjugateIrregular(person, number, tense, depForm, root) {
  var word = SAMPLE_VERBS.find(w => w.verb === root);
  var index = getTableIndex(person, number);
  if (word) {
    switch (tense) {
      case VERB_TENSES.present:
        var table = word.tables.find(t => t.tense == VERB_TENSES.present);
        var result;
        if(depForm === VERB_FORMS.independent){
          result = table.independent_forms[index]
        }
        else{
          result = table.dependent_forms[index]
          result = depForm === VERB_FORMS.negative ? result.map(handleNegative) : result.map(handleInterrogative); //will need to revise this if we ever get a table with single entries in a cell
        }
        return result;
      default:
        return ""
    }
  }
}

function conjugateSubjunctive(root){
  return "go " + eclipse(root)
}

export function conjugate(person, number, category, tense, depForm, root) {
  var result = [];
  var negative = depForm === VERB_FORMS.negative;
  var inter = depForm === VERB_FORMS.interrogative;
  if (category === "irregular") {
    return conjugateIrregular(person, number, tense, depForm, root);
  }
  var endings = verbEnding(category, tense, person, number, negative || inter);
  for (var i = 0; i < endings.length; i++) {
    result.push(handlePrefix(tense, person, number, i, endings, root, negative, inter));
  }
  return result;
}

function handlePrefix(tense, person, number, i, endings, root, negative, inter) {
  var conjugatedForm = "";
  if (tense === VERB_TENSES.past || tense === VERB_TENSES.habitual) {
    var isSPreterite = person === VERB_PERSON.third && number === VERB_NUMBER.singular && i === endings.length - 1 && tense !== VERB_TENSES.habitual;
    conjugatedForm = pastTense(root, negative, inter, isSPreterite);
  } else if (tense === VERB_TENSES.present || tense === VERB_TENSES.future || tense === VERB_TENSES.imperative || tense === VERB_TENSES.conditional && (inter || negative)) {
    conjugatedForm = defaultConjugation(root, negative, inter, tense);
  }
  else if (tense === VERB_TENSES.conditional) {
    if (!negative && !inter)
      conjugatedForm += past_tense_prefix + " " + leniteRoot(root);
  } else if (tense === VERB_TENSES.subjunctive) {
    conjugatedForm = conjugateSubjunctive(root);
  }
  conjugatedForm = handleEnding(endings[i], conjugatedForm);
  if (inter)
    conjugatedForm += "?";
  return conjugatedForm;
}

function handleEnding(ending, conjugatedForm) {
  if (letterIs(firstLetter(ending), "t") &&
    !letterIs(lastLetter(conjugatedForm), "dntls"))
    conjugatedForm += lenite(ending);
  else if (endsWith(conjugatedForm, "s")) {
    if (beginsWithS(ending)) {
      conjugatedForm += ending.substring(1);
    }
    else if (startsWith(ending, "(s)"))
      conjugatedForm += ending.substring(3);
    else
      conjugatedForm += ending;
  } else {
    conjugatedForm += ending;
  }
  return conjugatedForm;
}

function leniteRoot(root) {
  var first = firstLetter(root);
  if (letterIsVowel(first) || letterIsNRL(first)) return root;
  else if (letterIsS(first)) {
    var secondLetter = root.substring(1, 2);
    if (letterIsCFGMP(secondLetter)) return root;
  }
  return first + "h" + root.substring(1);
}

function lenite(str) {
  return firstLetter(str) + "h" + str.substring(1);
}

const ECLIPSE_MAPPING = [["b","c","d","f","g","p","t"],["m","g","n","bh","n","b","d"]]

function eclipse(root) {
  var i = ECLIPSE_MAPPING[0].indexOf(firstLetter(root));
  if(i < 0) return root;
  return ECLIPSE_MAPPING[1][i] + root
}

function verbEnding(category, tense, person, number, isDependent) {
  var mapTense = conjugationTables.mapping.find(table => table.tense === tense);
  if(!mapTense) console.error("Could not find matching ending table")
  var endingsByCategory = isDependent
    ? mapTense.dependent
    : mapTense.independent;
  var catIndex = Object.values(VERB_CATEGORY).indexOf(category);
  if (catIndex < 0) console.error("The category for the verb was not found");
  var endings = endingsByCategory[catIndex];
  var index = getTableIndex(person,number);
  return endings[index];
}

export function allFormsOfWord(word) {
  var newWord = "";
  var newWordArray = [];
  var optCount = 0;
  // check how many instances of () there are total...
  for (var i = 0; i < word.length; i++) {
    if (word.charAt(i) == "(") {
      optCount++;
    }
  }
  if (optCount > 0) {
    // all parentheses letters included
    newWord = word.split("(").join("");
    newWord = newWord.split(")").join("");
    newWordArray.push(newWord);
    // no parenteses letters included, using regex
    newWord = word.replace(/ *\([^)]*\) */g, "");
    newWordArray.push(newWord);
    // each version
    if (optCount > 1) {
      while (optCount > 0) {
        var parIndex = word.split("(", optCount).join("(").length;
        // forgive me for this abomination ;;
        var tempWord =
          word.slice(0, parIndex) +
          word.slice(parIndex + 1, parIndex + 2) +
          word.slice(parIndex + 3);
        newWord = tempWord.replace(/ *\([^)]*\) */g, "");
        newWordArray.push(newWord);
        optCount--;
      }
    }
  } else {
    newWordArray.push(word);
  }
  return newWordArray;
}

export function removeAsterisk(word) {
  word = word.replace("*", "");
  return word;
}

export function removeQuestionMark(word) {
  word = word.replace("?", "");
  return word;
}

export function simplifyWord(word) {
  word = removeQuestionMark(removeAsterisk(word)).toLowerCase();
  return word;
}

export function genSet(setLength){
  let promptSet = new Set()
    for (var i = 0; i < setLength; i++){
      promptSet.add(randomConjugationPrompt(_.sample(SAMPLE_VERBS)));
    }
    return promptSet;
}
